<template>
  <body>
    <img class="logo_stattus" src="../assets/LogoStattus4Verde.png" alt="Logo">
  </body>
</template>

<script>
import { onMounted } from 'vue';
import axios from 'axios';
import { userFindOneInfo } from "../services/userInfo";
import { useLoading } from 'vue-loading-overlay';
import { store } from '../services/store.js';
import { useRouter, useRoute } from 'vue-router';
import { getRealmByDisplayUrl } from '../services/realmList.js';
import { generateCodeVerifier, generateCodeChallenge } from '../utils/pkce';

const $loading = useLoading();

export default {
  name: 'Intercept',
  setup() {
    const router = useRouter();
    const route = useRoute();

    const decodeJwt = (token) => {
      try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
          atob(base64)
            .split('')
            .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join('')
        );
        return JSON.parse(jsonPayload);
      } catch (e) {
        return null;
      }
    };

    const doLogin = async (email, token) => {
      const loader = $loading.show({});
      await userFindOneInfo(email, token)
        .then((response) => {
          const path = window.location.pathname;
          const partPath = path.split('/');
          const client = partPath[partPath.length - 1];
          localStorage.setItem('last_client', client);
          save(response, token);
          success(response, token);
          loader.hide();
        })
        .catch(() => {
          loader.hide();
        });
    };

    const save = (data, token, extra = {}) => {
      const baseUserData = {
        client_id: data.data.client_id,
        token: token,
        user_id: data.data.id,
        name: data.data.name,
        type_user: data.data.type_user,
        trading_name: data.data.client.trading_name,
        last_search: '',
        last_search_name: '',
        roles: data.data.roles,
        demo: data.data.demo,
        login_method: extra.login_method || 'traditional',
        keycloak_iss: extra.keycloak_iss || null,
        keycloak_realm: extra.keycloak_realm || null,
        keycloak_display: extra.keycloak_display || null,
      };

      const userData = { ...baseUserData };
      localStorage.setItem('userData', JSON.stringify(userData));
    };

    const success = (response, token) => {
      store.client_id = response.data.client_id;
      store.token = token;
      store.user_id = response.data.id;
      store.name = response.data.name;
      store.type_user = response.data.type_user;
      store.trading_name = response.data.client.trading_name;
      store.last_search = '';
      store.last_search_name = '';
      store.roles = response.data.roles;
      store.demo = response.data.demo;
      if (store.type_user == 3) {
        router.replace('/listagem/ativacao');
      } else {
        router.replace('/dashboard');
      }
    };

    const loginWithAccessToken = async (accessToken) => {
      const payload = decodeJwt(accessToken);
      if (!payload) {
        return;
      }

      const email = payload.email || payload.preferred_username || '';
      if (!email) {
        return;
      }

      const loader = $loading.show({});
      try {
        const response = await userFindOneInfo(email, accessToken);

        const iss = payload.iss || '';
        let realmFromIss = null;
        if (iss.includes('/realms/')) {
          realmFromIss = iss.split('/realms/')[1] || null;
        }

        const extra = {
          login_method: 'keycloak',
          keycloak_iss: iss || null,
          keycloak_realm: realmFromIss,
          keycloak_display: route.params.realm || realmFromIss,
        };

        save(response, accessToken, extra);
        success(response, accessToken);
      } catch (e) {
        console.error('Erro ao buscar informações do usuário com access_token.', e);
      } finally {
        loader.hide();
      }
    };

    onMounted(async () => {
      const hash = window.location.hash || '';
      if (hash.startsWith('#')) {
        const queryString = hash.substring(1);
        const searchParams = new URLSearchParams(queryString);
        const code = searchParams.get('code');
        const iss = searchParams.get('iss');
        const error = searchParams.get('error');
        const errorDesc = searchParams.get('error_description');

        if (error) {
          console.error('Keycloak authentication error in Intercept:', error, errorDesc);
          router.replace('/login');
          return;
        }

        if (code && iss) {
          try {
            const clientId = process.env.VUE_APP_CLIENT_ID;
            const redirectUri = `${window.location.origin}${route.fullPath.split('#')[0]}`;
            const tokenUrl = `${iss}/protocol/openid-connect/token`;

            const codeVerifier = sessionStorage.getItem('turing_pkce_verifier');
            if (codeVerifier) {
              sessionStorage.removeItem('turing_pkce_verifier');
            }

            const body = new URLSearchParams({
              grant_type: 'authorization_code',
              code,
              client_id: clientId,
              redirect_uri: redirectUri,
            });
            if (codeVerifier) {
              body.append('code_verifier', codeVerifier);
            }

            const res = await axios.post(tokenUrl, body, {
              headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            });

            const accessToken = res.data && res.data.access_token ? res.data.access_token : '';
            if (accessToken) {
              await loginWithAccessToken(accessToken);
              return;
            }
          } catch (e) {
            console.error('Erro ao obter access_token do Keycloak em Intercept.', e);
          }
        }
      }

      const url = new URL(window.location.href);
      const email = url.searchParams.get('email');
      const token = url.searchParams.get('token');

      const realmParam = route.params.realm;
      if (realmParam) {
        const baseKeycloak = process.env.VUE_APP_URL_BASE_KEYCLOAK;
        const clientId = process.env.VUE_APP_CLIENT_ID;

        if (baseKeycloak && clientId) {
          let keycloakRealm = realmParam;

          try {
            const byDisplayUrl = await getRealmByDisplayUrl(realmParam);
            if (byDisplayUrl && byDisplayUrl.realm) {
              keycloakRealm = byDisplayUrl.realm;
            }
          } catch (e) {
            console.error('Erro ao carregar realmList da API.', e);
          }

          const redirectUrl = `${window.location.origin}${route.fullPath.split('#')[0]}`;
          const codeVerifier = generateCodeVerifier();
          const codeChallenge = await generateCodeChallenge(codeVerifier);
          sessionStorage.setItem('turing_pkce_verifier', codeVerifier);

          const params = new URLSearchParams({
            client_id: clientId,
            redirect_uri: redirectUrl,
            response_mode: 'fragment',
            response_type: 'code',
            scope: 'openid',
            code_challenge: codeChallenge,
            code_challenge_method: 'S256',
          });
          const target = `${baseKeycloak}/realms/${encodeURIComponent(keycloakRealm)}/protocol/openid-connect/auth?${params.toString()}`;
          window.location.href = target;
          return;
        }
      }

      if (email && token) {
        doLogin(email, token);
        return;
      }

      const aux = localStorage.getItem('userData');
      const userData = aux ? JSON.parse(aux) : null;
      if (userData && userData.token) {
        if (userData.type_user == 3) {
          router.replace('/listagem/ativacao');
        } else {
          router.replace('/dashboard');
        }
        return;
      }

      router.replace('/login');
    });

    return { doLogin };
  }
};
</script>

<style scoped>
body{
  height: 110vh;
  background-image: url('../assets/inteligentes.jpg');
  background-repeat: no-repeat;
  background-size: cover;
}
.logo_stattus{
  margin: 2rem 2rem 3.5rem 2rem;
  width: 27rem;
  height: 6.5rem;
}
.img_persona{
  height: 9rem;
  position: relative;
  margin-top: -6rem;
}
form{
  display: flex;
  flex-direction: column;
  align-items: center;
}
.card-body {
  display: flex;
  flex: 1 1 auto;
  padding: 1rem 1rem;
  flex-direction: column;
  align-items: center;
}
@import url('https://unpkg.com/css.gg@2.0.0/icons/css/user.css');
.gg-user {
  display: block;
  transform: scale(var(--ggs,2));
  box-sizing: border-box;
  width: 13px;
  height: 20px;
  color: white;
}
@import url('https://unpkg.com/css.gg@2.0.0/icons/css/lock.css');
.gg-lock {
  box-sizing: border-box;
  position: relative;
  display: block;
  transform: scale(var(--ggs,1.5));
  width: 12px;
  height: 11px;
  border: 2px solid;
  border-top-right-radius: 50%;
  border-top-left-radius: 50%;
  border-bottom: transparent;
  margin-top: -12px;
  color: white;
}
.bottom-options{
  display: flex;
  margin: 0rem;
  font-size: 66.5%;
}
.btn-login{
  border-color: #6eff94;
  background-color: #6eff94;
  color: black;
  line-height: 2rem;
  font-size: 1.5rem;
  margin:1rem
}
.input-group>.form-control, .input-group>.form-select{
  background-color: #41464b96;
  color: white;
  line-height: 3rem
}
.input-group{
  margin: .5rem 1rem
}
input::placeholder {
  color: #fff;
}
.input-group-text{
  background-color: #37393c;
  padding: 0.375rem 1.2rem;
}
.card{
  border-radius: 2.25rem;
  padding: 1rem;
  margin-bottom: 3rem;
}
.form-check-input{
  background-color: #41464b96;
}
.form-check-input:checked {
  background-color: #6EFF94;
  border-color: #6EFF94;
}
.form-check{
  margin-right: 9.5rem
}
a {
  color: #000000;
  text-decoration: underline;
}
@media only screen and (max-width: 599px) {
  body{
    height: 110vh;
    text-align: center;
    background-image: url(/img/inteligentes.bb8c352c.jpg);
    background-repeat: no-repeat;
    background-size: cover;
  }

  .logo_stattus{
    margin: 2rem 0.5rem 3.5rem 0.5rem;
    width: 95%;
    height: 6.5rem;
  }
}
@media only screen and (min-width: 575px) and (max-width: 875px) { 
  #main_box{
    width: 52%;
  }
}
@media only screen and (min-width: 600px) and (max-width: 865px) { 
  .logo_stattus{
    margin: 2rem 2rem 4.5rem 1rem;
    width: 20rem;
    height: 4.5rem;
  }
}
@media only screen and (min-width: 480px) and (max-width: 599px) { 
  .logo_stattus{
    margin: 2rem 0.5rem 3.5rem 0.5rem;
    width: 80%;
    height: 6.5rem;
  }
}
@media only screen and (min-height: 730px) {
  .logo_stattus{
    margin: 2rem 2rem 8.5rem 2rem;
  }
}
</style>
